import { useCallback, useEffect, useState } from "react";
import TopTemplate from "../../common/templates/top/TopTemplate";
import { PageTitle } from "../../common/PageTitle";
import { Box, BoxBackGround } from "../../common/Box";
import {
  ButtonPriority,
  ButtonType,
  ButtonWidth,
  ButtonWrapper,
  TextSize,
} from "../../common/Button";
import { Heading, HeadingType } from "../../common/Heading";
import {
  UnOrderedList,
  UnOrderedListItem,
} from "../../common/List/UnOrderedList/UnOrderedList";
import { Text, TextType } from "../../common/Text";
import { ModLink } from "../../common/Link";
import { useHistory } from "react-router";
import {
  Table,
  TableCell,
  TableHeader,
  TableRow,
  TableStyle,
} from "../../common/Table";
import axios from "axios";
import { config } from "../../common/config/Config";

import style from "./AuthenticationList.module.css";
import { DateFormater as format } from "../../common/DateFormat/DateFormat";
import { ModalModule } from "../../common/Modal";
import { TextBox } from "../../common/Form/TextBox";
import { LoadingIcon } from "../../common/LoadingIcon/LoadingIcon";
import createUrl, { DOMAIN } from "../../common/urlCreator/urlCreator";
import { getUrlConfig } from "../../common/urlCreator/urlConfig";
import { validateTermName } from "../../common/TerminalNameValidator/termValidator";
import BlankIcon from "../../assets/img/icn-_blank.svg";
import {
  BIOMETRIC_INFO_PAGE_URL,
  BIOMETRIC_INFO_PAGE_URL_WITH_ANCHOR_03,
  BROWSER_NOT_SUPPORT_ERROR,
  EMPTY_TERMINAL_NAME_ERROR,
  FEATURE_NOT_AVAILABLE_ERROR,
  GET_LIST_AUTHENTICATOR_ERROR,
  MAX_DEVICE_ERROR,
  SYSTEM_ERROR,
  TERMINAL_NAME_VALIDATOR_FAILED_ERROR,
} from "../../common/Constant/Constant";

interface Authenticator {
  id: string;
  deviceName: string;
  registerDate: string;
}

enum ModalType {
  DEFAULT = 0,
  DELETE = 1,
  EDIT = 2,
}

const initAuthenticator: Authenticator = {
  id: "",
  deviceName: "登録なし	",
  registerDate: "日時がありません",
};

const initalAuthenticators = () => {
  return new Array(config.maxNumberRegisterDevice).fill(initAuthenticator);
};

const urlConfig = getUrlConfig();

const AuthenticatorManager = () => {
  const history = useHistory();
  const [authenticators, setAuthenticators] =
    useState<Authenticator[]>(initalAuthenticators);
  const [openModal, setOpenModal] = useState(false);
  const [modalType, setModalType] = useState(ModalType.DEFAULT);
  const [selectedAuthenticator, setSelectedAuthenticator] =
    useState<Authenticator>(initAuthenticator);
  const [authenticatorCount, setAuthenticatorCount] = useState(0);
  const [commonErrorMessage, setCommonErrorMessage] = useState("");
  const [browserNotSupportError, setBrowserNotSupportError] = useState(false);
  const [isMaxRegisteredDevice, setIsMaxRegisteredDevice] = useState(false);
  const [showError, setShowError] = useState(false);
  const [loadingState, setLoadingState] = useState(false);
  const [userSupportedState, setUserSupportedState] = useState(true);
  const [showRegisterUnsupportedError, setShowRegisterUnsupportedError] =
    useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [modalErrorMessage, setModalErrorMessage] = useState("");
  const [modalShowError, setModalShowError] = useState(false);

  useEffect(() => {
    // Scroll to anchor position.
    const selected = document.location.hash
    if(selected && selected.length > 0) {
      const elem = document.querySelector(selected)
      elem && elem.scrollIntoView()
    }
  })

  useEffect(() => {
    retrieveAuthenticators();
  }, []);

  useEffect(() => {
    if (!openModal && isProcessing) {
      // Re-active OK button after close update/delete modal
      setIsProcessing(false);
    }
  }, [openModal]);

  const retrieveAuthenticators = useCallback(async () => {
    try {
      setLoadingState(true);
      const res = await axios.post(
        `${config.apiBaseUrl}${config.apiMemberAuthenticatorGet}`,
        {}
      );

      const data = res.data;

      const authenticators = data.authenticators.map(
        (item: any): Authenticator => ({
          id: item.credential_id,
          deviceName: item.authenticator_name,
          registerDate:
            item.created_at &&
            format(item.created_at),
        })
      ).sort((n1: any, n2: any) => {
        if (n1.registerDate < n2.registerDate) {return 1;}
        if (n1.registerDate > n2.registerDate) {return -1;}
        return 0;
      });

      const displayAuthenticators = [
        ...authenticators,
        ...initalAuthenticators(),
      ].slice(0, config.maxNumberRegisterDevice);

      setAuthenticatorCount(data.count);
      setAuthenticators(displayAuthenticators);
    } catch (err:any) {
      if (err.response.status === 403) {
        setUserSupportedState(false);
        setLoadingState(false);
        return;
      }
      setCommonErrorMessage(GET_LIST_AUTHENTICATOR_ERROR);
      setShowError(true);
    } finally {
      setLoadingState(false);
    }
  }, []);

  const onUpdateButtonClick = useCallback((authenticator: Authenticator) => {
    setSelectedAuthenticator(authenticator);
    setModalType(ModalType.EDIT);
    setOpenModal(true);
  }, []);

  const onDeleteButtonClick = useCallback((authenticator: Authenticator) => {
    setSelectedAuthenticator(authenticator);
    setModalType(ModalType.DELETE);
    setOpenModal(true);
  }, []);

  const onModalCloseButtonClick = useCallback(() => {
    setOpenModal(false);
    setModalShowError(false);
  }, []);

  const onUpdateModalSubmitButtonClick = useCallback(
    (newAuthenticatorName: string) => {
      setShowError(false);
      const newName = newAuthenticatorName.trim();
      if (!newName) {
        setModalErrorMessage(EMPTY_TERMINAL_NAME_ERROR);
        setModalShowError(true);
        return;
      }
      if (!validateTermName(newName)) {
        setModalErrorMessage(TERMINAL_NAME_VALIDATOR_FAILED_ERROR);
        setModalShowError(true);
        return;
      }
      setIsProcessing(true);
      const updateAuthenticator = async () => {
        try {
          await axios.post(
            `${config.apiBaseUrl}${config.apiMemberAuthenticatorUpdate}`,
            {
              old_name: selectedAuthenticator.deviceName,
              credential_id: selectedAuthenticator.id,
              authenticator_name: newName,
            }
          );
        } catch (err) {
          showSystemError();
        } finally {
          setOpenModal(false);
          setModalShowError(false);
          retrieveAuthenticators();
        }
      };
      updateAuthenticator();
    },
    [selectedAuthenticator]
  );

  const onDeleteModalSubmitButtonClick = useCallback(() => {
    setShowError(false);
    setIsProcessing(true);
    const deleteAuthenticator = async () => {
      try {
        await axios.post(
          `${config.apiBaseUrl}${config.apiMemberAuthenticatorDelete}`,
          {
            authenticator_name: selectedAuthenticator.deviceName,
            credential_id: selectedAuthenticator.id,
          }
        );
        retrieveAuthenticators();
      } catch (err) {
        showSystemError();
      } finally {
        setOpenModal(false);
      }
    };
    deleteAuthenticator();
  }, [selectedAuthenticator]);

  const onRegisterButtonClick = () => {
    setBrowserNotSupportError(false);
    setIsMaxRegisteredDevice(false);
    setShowError(false);
    if (!window.PublicKeyCredential) {
      setBrowserNotSupportError(true);
      setShowError(true);
      return;
    }
    if (authenticatorCount >= config.maxNumberRegisterDevice) {
      setShowError(true);
      setIsMaxRegisteredDevice(true);
      return;
    }
    if (!userSupportedState) {
      setShowRegisterUnsupportedError(true);
      return;
    }
    history.push("/bio-registration");
  };

  const showSystemError = () => {
    setCommonErrorMessage(SYSTEM_ERROR);
    setShowError(true);
  };

  if (showRegisterUnsupportedError) {
    return (
      <TopTemplate>
        <MemberNotSupportedErrorPresentation
          onBackButtonClick={() => {
            setShowRegisterUnsupportedError(false);
          }}
        />
      </TopTemplate>
    );
  }

  return (
    <>
      <TopTemplate>
        <>
          <PageTitle text="生体認証ログイン設定" />
          {showError ? (
            browserNotSupportError ? (
              <BrowserNotSupportErrorMessageForm />
            ) : (
              isMaxRegisteredDevice ? (
                <MaxDeviceErrorMessageForm />
              ) : (
                <ErrorMessageForm errorMessages={commonErrorMessage} />
              )
            )
          ) : null}
          <Heading
            type={HeadingType.B}
            level={3}
            text="指紋・顔認証のログイン設定"
          />
          <Text
            textItems={[
              {
                text: "生体認証機能が搭載された端末を登録することで、パスワードを入力する代わりに、指紋・顔認証などを使ってログインできるようになります。",
              },
            ]}
            adjust={{ mb: 8 }}
          />
          <ModLink
            href={BIOMETRIC_INFO_PAGE_URL}
            activeForAgent={true}
            children="生体認証の詳細はこちら"
          />
          <ButtonWrapper
            textSize={TextSize.NORMAL}
            buttons={[
              {
                text: "この端末を登録する",
                type: ButtonType.BUTTON,
                width: ButtonWidth.WIDE,
                priority: ButtonPriority.HIGH,
                disabled: loadingState,
                onClick: () => {
                  onRegisterButtonClick();
                },
              },
            ]}
          />
          <Heading
            type={HeadingType.B}
            level={3}
            text="登録済みの端末"
            id="anc-01"
          />
          <AuthenticatorTablePresentation
            isLoading={loadingState}
            authenticators={authenticators}
            onUpdateButtonClick={onUpdateButtonClick}
            onDeleteButtonClick={onDeleteButtonClick}
          />
          <Text
            textItems={[
              {
                type: TextType.SYMBOL,
                text: `${config.maxNumberRegisterDevice}台まで登録できます。`,
              },
            ]}
            adjust={{ mb: 8 }}
            className={style["text-hint"]}
          />
          <ButtonWrapper
            textSize={TextSize.NORMAL}
            buttons={[
              {
                text: "マイページに戻る",
                type: ButtonType.LINK,
                width: ButtonWidth.WIDE,
                priority: ButtonPriority.BACK,
                href: createUrl(DOMAIN.SONET, `${urlConfig.SONET_MYPAGE_PATH}`),
                activeForAgent: true,
              },
            ]}
          />
        </>
        <ModalModule isOpen={openModal} onClose={onModalCloseButtonClick}>
          {ModalType.DELETE === modalType ? (
            <DeleteForm
              authenticator={selectedAuthenticator}
              onCloseButtonClick={onModalCloseButtonClick}
              onSubmitButtonClick={onDeleteModalSubmitButtonClick}
              isProcessing={isProcessing}
              ErrorMessage={modalErrorMessage}
              modalShowError={modalShowError}
            />
          ) : ModalType.EDIT ? (
            <UpdateForm
              authenticator={selectedAuthenticator}
              onCloseButtonClick={onModalCloseButtonClick}
              onSubmitButtonClick={onUpdateModalSubmitButtonClick}
              isProcessing={isProcessing}
              ErrorMessage={modalErrorMessage}
              modalShowError={modalShowError}
            />
          ) : null}
        </ModalModule>
      </TopTemplate>
    </>
  );
};

interface AuthenticationTableProps {
  isLoading: boolean;
  authenticators: Authenticator[];
  onUpdateButtonClick: Function;
  onDeleteButtonClick: Function;
}

const AuthenticatorTablePresentation = (props: AuthenticationTableProps) => {
  const authenticators = props.authenticators;

  return (
    <>
      <Table
        style={TableStyle.DEFAULT}
        header={{
          items: [
            { children: "端末名" },
            { children: "登録日時" },
            { children: " ", colSpan: 2 },
          ],
        }}
      >
        {props.isLoading ? (
          <TableRow>
            <TableCell>
              <LoadingIcon />
            </TableCell>
            <TableCell>
              <LoadingIcon />
            </TableCell>
            <TableCell colSpan={2}>
              <LoadingIcon />
            </TableCell>
          </TableRow>
        ) : (
          authenticators.map((item, index) => (
            <TableRow key={item.id != "" ? item.id : index}>
              <TableCell>{item.deviceName}</TableCell>
              <TableCell>{item.registerDate}</TableCell>
              <TableCell colSpan={2}>
                {item.id && (
                  <ButtonWrapper
                    className={style["button-wrapper"]}
                    textSize={TextSize.SMALL}
                    col={1}
                    colPc={2}
                    buttons={[
                      {
                        text: "端末名の変更",
                        priority: ButtonPriority.MEDIUM,
                        onClick: () => props.onUpdateButtonClick(item),
                      },
                      {
                        text: "削除",
                        priority: ButtonPriority.LOW,
                        onClick: () => props.onDeleteButtonClick(item),
                      },
                    ]}
                  />
                )}
              </TableCell>
            </TableRow>
          ))
        )}
      </Table>
    </>
  );
};

interface UpdateDeleteFormProps {
  authenticator: Authenticator;
  onCloseButtonClick: Function;
  onSubmitButtonClick: Function;
  isProcessing: boolean;
  ErrorMessage: string;
  modalShowError: boolean;
}

const UpdateForm = (props: UpdateDeleteFormProps) => {
  const authenticator = props.authenticator;
  const [newName, setNewName] = useState("");
  const modalErrorMessage = props.ErrorMessage;
  const showError = props.modalShowError;

  return (
    <>
      <Text
        textItems={[
          {
            text: "新しい認証機名を入力してください。",
          },
        ]}
        adjust={{ mb: 8 }}
      />
      <Text
        textItems={[
          {
            type: TextType.SYMBOL,
            text: "32文字以内",
          },
          {
            type: TextType.SYMBOL,
            text: "利用可能な文字は、半角のアルファベット小文字・大文字、数字、半角スペース、-(ハイフン)、_(アンダーバー)と.(ドット)になります",
          },
        ]}
        adjust={{ mb: 8 }}
        className={style["text-hint"]}
      />

      {showError ? (<ErrorMessageForm errorMessages={modalErrorMessage} />) : null}

      <Table style={TableStyle.TYPE2}>
        <TableRow>
          <TableHeader>現在</TableHeader>
          <TableCell colSpan={3}>{authenticator.deviceName}</TableCell>
        </TableRow>
        <TableRow>
          <TableHeader>変更後</TableHeader>
          <TableCell colSpan={3}>
            <TextBox
              maxLength={32}
              required={true}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
          </TableCell>
        </TableRow>
      </Table>
      <ButtonWrapper
        textSize={TextSize.SMALL}
        buttons={[
          {
            text: "OK",
            type: ButtonType.BUTTON,
            width: ButtonWidth.NORMAL,
            priority: ButtonPriority.MEDIUM,
            disabled: props.isProcessing,
            onClick: () => props.onSubmitButtonClick(newName),
          },
          {
            text: "キャンセル",
            type: ButtonType.BUTTON,
            width: ButtonWidth.NORMAL,
            priority: ButtonPriority.LOW,
            onClick: () => props.onCloseButtonClick(),
          },
        ]}
      />
    </>
  );
};

const DeleteForm = (props: UpdateDeleteFormProps) => {
  const authenticator = props.authenticator;

  return (
    <>
      <Text
        textItems={[
          {
            text: `「${authenticator.deviceName}」を削除してもよろしいですか？削除した場合、該当の認証機で生体認証を行うには再度登録が必要となります。`,
          },
        ]}
      />

      <ButtonWrapper
        textSize={TextSize.SMALL}
        buttons={[
          {
            text: "OK",
            type: ButtonType.BUTTON,
            width: ButtonWidth.NORMAL,
            priority: ButtonPriority.MEDIUM,
            disabled: props.isProcessing,
            onClick: () => props.onSubmitButtonClick(),
          },
          {
            text: "キャンセル",
            type: ButtonType.BUTTON,
            width: ButtonWidth.NORMAL,
            priority: ButtonPriority.LOW,
            onClick: () => props.onCloseButtonClick(),
          },
        ]}
      />
    </>
  );
};

const MaxDeviceErrorMessageForm = () => {
  const maxDeviceErrorMessage = MAX_DEVICE_ERROR;
  const errorElement = [];
  errorElement.push(
    <UnOrderedListItem
      text={maxDeviceErrorMessage}
      children={
        <>
          <a href="#anc-01">登録済みの端末一覧</a>
          から不要な端末を削除してください。
        </>
      }
      textStrong={true}
    />
  );
  return (
    <Box background={BoxBackGround.ALERT}>
      <UnOrderedList children={errorElement} />
    </Box>
  );
};

const BrowserNotSupportErrorMessageForm = () => {
  const browserNotSupportErrorMessage = BROWSER_NOT_SUPPORT_ERROR;
  const errorElement = [];
  errorElement.push(
    <UnOrderedListItem
      text={browserNotSupportErrorMessage}
      children={
        <>
          <img
            src={BlankIcon}
            width="12"
            height="10"
            alt="新しいウィンドウで開く"
            className={`icn-_blank`}
          />
          <a href={BIOMETRIC_INFO_PAGE_URL_WITH_ANCHOR_03} target="_blank">利用可能な環境</a>
          をご確認の上、別のブラウザでお試しください。
        </>
      }
      textStrong={true}
    />
  );
  return (
    <Box background={BoxBackGround.ALERT}>
      <UnOrderedList children={errorElement} />
    </Box>
  );
};

const ErrorMessageForm = (props: { errorMessages: string }) => {
  const { errorMessages } = props;
  const errorElement = [];
  errorElement.push(
    <UnOrderedListItem text={errorMessages} textStrong={true} />
  );
  return (
    <Box background={BoxBackGround.ALERT}>
      <UnOrderedList children={errorElement} />
    </Box>
  );
};

const MemberNotSupportedErrorPresentation = (props: {
  onBackButtonClick: Function;
}) => {
  return (
    <>
      <PageTitle text="生体認証の登録" />
      <Text
        textItems={[
          {
            text: FEATURE_NOT_AVAILABLE_ERROR,
          },
        ]}
      />
      <ButtonWrapper
        textSize={TextSize.SMALL}
        buttons={[
          {
            text: "戻る",
            type: ButtonType.LINK,
            priority: ButtonPriority.BACK,
            onClick: () => {
              props.onBackButtonClick();
            },
          },
        ]}
      />
    </>
  );
};

export { AuthenticatorManager };
